import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ViewMode = 'customer' | 'admin';

interface ViewModeContextValue {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  toggleAdminView: () => void;
}

const ViewModeContext = createContext<ViewModeContextValue | undefined>(undefined);

export const ViewModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [viewMode, setViewModeState] = useState<ViewMode>(() =>
    window.location.pathname.startsWith('/admin') ? 'admin' : 'customer'
  );

  const setViewMode = useCallback((mode: ViewMode) => setViewModeState(mode), []);
  const toggleAdminView = useCallback(() => setViewModeState(prev => (prev === 'admin' ? 'customer' : 'admin')), []);

  const value = useMemo<ViewModeContextValue>(
    () => ({ viewMode, setViewMode, toggleAdminView }),
    [viewMode, setViewMode, toggleAdminView]
  );

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export const useViewMode = () => {
  const ctx = useContext(ViewModeContext);
  if (!ctx) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return ctx;
};
